/**
 * SupplierShipLogPage.jsx
 * ───────────────────────
 * Screen 2 — Shipment log for a selected project.
 * Route: /supplier/disposal/:projectId/log
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../../shared/context/UserContext';
import { getProjectBols, getSupplierProjects } from '../../api/supplierDisposalApi';
import SupplierLayout from '../../layouts/SupplierLayout';
import './SupplierShipLogPage.css';

function fmtDatetime(v) {
  if (!v) return '';
  try {
    return new Date(v).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return v; }
}

function bolStatusClass(status) {
  const s = (status || '').toUpperCase();
  if (s === 'OPEN' || s === 'LOADING' || s === 'LOADED') return 'sl-badge-blue';
  if (s === 'CLOSED') return 'sl-badge-grey';
  if (s === 'REOPENED' || s === 'AMENDED') return 'sl-badge-orange';
  return 'sl-badge-grey';
}

export default function SupplierShipLogPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { callerEmail } = useUser();

  const [bols, setBols] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!callerEmail || !projectId) return;
    setLoading(true);
    setError(null);

    Promise.all([
      getProjectBols(callerEmail, projectId),
      getSupplierProjects(callerEmail),
    ])
      .then(([bolData, projData]) => {
        const bolList = Array.isArray(bolData) ? bolData : bolData.results || bolData.bols || [];
        setBols(bolList);
        const projList = Array.isArray(projData) ? projData : projData.projects || projData.results || [];
        const match = projList.find(p => p.project_id === projectId);
        setProject(match || null);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [callerEmail, projectId]);

  const oem = project?.oem || '';
  const projectNumber = project?.project_number || projectId;

  function handleBolTap(bol) {
    const s = (bol.status || '').toUpperCase();
    if (s === 'CLOSED' || s === 'AMENDED') {
      navigate(`/supplier/disposal/${projectId}/bol/${bol.bol_id}/edit`, {
        state: { bol, project },
      });
    }
  }

  return (
    <SupplierLayout>
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="slp-header">
        <span className="slp-header-title">
          {oem ? `${oem} — ${projectNumber}` : projectNumber}
        </span>
        <span className="slp-header-logo">TMG</span>
      </div>

      <div className="slp-subtitle">
        Shipment Log — {bols.length > 0 ? `${bols.length} shipment${bols.length !== 1 ? 's' : ''}` : 'no shipments yet'}
      </div>

      {/* ── Content ───────────────────────────────────────────────────── */}
      <div className="slp-content">
        {loading ? (
          <div className="slp-state">Loading shipments...</div>
        ) : error ? (
          <div className="slp-state slp-state-error">{error}</div>
        ) : bols.length === 0 ? (
          <div className="slp-empty">
            <div className="slp-empty-title">No shipments yet for this project.</div>
            <div className="slp-empty-sub">Load tools and close a shipment to see it here.</div>
          </div>
        ) : (
          <div className="slp-bol-list">
            {bols.map(bol => {
              const s = (bol.status || '').toUpperCase();
              const isClosed = s === 'CLOSED' || s === 'AMENDED';
              const isManual = bol.source === 'MANUAL';
              const toolCount = bol.tool_count || bol.tools?.length || 0;

              return (
                <div
                  key={bol.bol_id}
                  className={`slp-bol-row ${isClosed ? 'slp-bol-tappable' : ''}`}
                  onClick={() => handleBolTap(bol)}
                >
                  {/* Row 1 */}
                  <div className="slp-bol-row1">
                    <span className="slp-bol-number">
                      {bol.bol_number || bol.bol_id?.substring(0, 12)}
                      {isManual && <span className="slp-manual-badge">MANUAL</span>}
                    </span>
                    <span className={`slp-bol-badge ${bolStatusClass(s)}`}>{s}</span>
                  </div>

                  {/* Row 2 */}
                  <div className="slp-bol-row2">
                    <span className="slp-bol-date">{fmtDatetime(bol.created_at || bol.closed_at)}</span>
                    <span className="slp-bol-truck">{bol.truck_type || ''}</span>
                  </div>

                  {/* Row 3 */}
                  <div className="slp-bol-row3">
                    <span className="slp-bol-info">
                      {bol.truck_number ? `#${bol.truck_number}` : ''}
                      {bol.driver_name ? ` — ${bol.driver_name}` : ''}
                    </span>
                    <span className="slp-bol-weight">
                      {toolCount} tool{toolCount !== 1 ? 's' : ''}
                      {bol.estimated_weight_lbs ? ` · ${Number(bol.estimated_weight_lbs).toLocaleString()} lbs` : ''}
                    </span>
                  </div>

                  {/* Closed hint */}
                  {isClosed && (
                    <div className="slp-bol-hint">Tap to reopen &rarr;</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Bottom bar ────────────────────────────────────────────────── */}
      <div className="slp-bottom-bar">
        <button className="slp-back-btn" onClick={() => navigate('/supplier/disposal')}>
          &larr; Back
        </button>
      </div>
    </SupplierLayout>
  );
}
